import { observer } from 'mobx-react-lite';

import { DetailItem } from 'components/details/DetailItem';
import { DetailList } from 'components/details/DetailList';
import { EditNumber } from 'components/forms/EditNumber';
import { EditSelect, type EditSelectOptionList } from 'components/forms/EditSelect';
import { RETENTION_TYPE_OPTIONS } from 'utils/retentionPolicies';
import type { RetentionType } from 'api/shared/retentionPolicy';

import type { EditableRetentionPolicy } from './EditableRetentionPolicy';

const STATUS_OPTIONS: EditSelectOptionList<string> = ['all', 'completed', 'failed', 'canceled', 'skipped', 'killed'].map((s) => ({
  key: s,
  value: s,
  label: s,
}));

// The retention_type label already names the unit ("Days to keep", "Executions to keep", …), so no separate unit suffix.
const TYPE_OPTIONS: EditSelectOptionList<RetentionType> = RETENTION_TYPE_OPTIONS.map((o) => ({ key: o.value, value: o.value, label: o.label }));

// Its own observer: DetailList renders each item inside a render-prop (outside its own reaction), so the field reads must be
// tracked here for edits to re-render the row. The three controls share the DetailItem content row and stretch evenly.
const RetentionPolicyRow = observer(({ policy }: { readonly policy: EditableRetentionPolicy }) => (
  <DetailItem onRemove={policy.delete} removeLabel="Remove policy">
    <div className="flex-fill">
      <EditSelect value={policy.status} onChange={(v) => (policy.status = v)} options={STATUS_OPTIONS} />
    </div>
    <div className="flex-fill">
      <EditSelect<RetentionType> value={policy.retentionType} onChange={(v) => (policy.retentionType = v)} options={TYPE_OPTIONS} />
    </div>
    <div className="flex-fill">
      <EditNumber value={policy.value} onChange={(v) => (policy.value = v)} min={0} />
    </div>
  </DetailItem>
));

interface Props {
  readonly policies: readonly EditableRetentionPolicy[];
  readonly onAdd: () => void;
}

/**
 * The retention-policies editor: a DetailList of single-line policy rows over an owner's `retentionPolicies` Aggregate
 * (add / remove / edit). Owner-agnostic — takes the children + add callback, so any editable entity that holds a retention
 * `Aggregate` (node definitions, zones, …) can render it. Order is not meaningful, so the list is not drag-reorderable.
 */
export const RetentionPolicyEditor = observer(({ policies, onAdd }: Props) => (
  <div className="mb-4">
    <h5 className="mb-2">Retention Policies</h5>
    <DetailList
      items={policies}
      getKey={(policy) => policy.key}
      onAdd={onAdd}
      addLabel="Add policy"
      renderItem={(policy) => <RetentionPolicyRow policy={policy} />}
    />
  </div>
));
